<template>
  <div class="page">
    <!-- Controls -->
    <div class="controls" style="grid-template-columns: 1fr;">
      <select :value="companyId" @change="$router.push({ name: 'TruckList', params: { companyId: $event.target.value } })">
        <option value="616b9deb-04ec-486c-ac55-191d6f9ffe85">NATURE DISPATCH LLC</option>
        <option value="92d854d6-9dff-458e-a4aa-76aa302ee150">YSA TRANSPORT LLC</option>
      </select>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
        <select v-model="filters.status">
          <option value="all">All</option>
          <option value="driving">Driving</option>
          <option value="online">Online</option>
          <option value="idle">Idle</option>
          <option value="offline">Offline</option>
        </select>

        <select v-model="filters.sort">
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>
      </div>
    </div>

    <div v-if="isFirstLoad" style="padding:12px; color:#9fb0c7;">Loading…</div>
    <div v-else-if="error" style="padding:12px; color:#fca5a5;">{{ error }}</div>

    <!-- List -->
    <ul class="list">
      <li v-for="t in sortedAndFiltered" :key="t.id" class="card">
        <div class="row">
          <div style="display: flex; gap: 15px; align-items: center">
            <span>
                <i class="fa-solid fa-location-arrow" style="color: #42b983"></i>
            </span>
            <span class="num">{{ t.number }}</span>
            <span :class="['speed', speedClass(t)]" style="font-size: 18px;font-weight: 500">{{ speedLabel(t) }}</span>
          </div>
          <a :href="t.liveShareLink" target="_blank" rel="noopener noreferrer" class="link-button"
             style="color: #34d399">
            Live location
          </a>
        </div>

        <div class="row small">
          <div style="display: flex; gap: 15px; align-items: center;font-size: 16px;">
              <span>
                <i class="fa-solid fa-location-dot"></i>
              </span>
            <span class="time">{{ t.location }}</span>
          </div>
          <span class="time">{{ t.lastSeen }}</span>
        </div>

        <div class="row small">
          <div style="display: flex; gap: 15px; align-items: center; font-weight: bold">
            <span :class="['pill', pillClass(t)]">{{ t.statusShort }}</span>
            <span class="driver">{{ t.driver }}</span>
          </div>
          <button class="btn-diagnostics" @click="toggleDiagnostics(t.id, t.driverId)">Diagnostics</button>
        </div>
        <!-- Diagnostics accordion -->
        <div v-if="expandedTruck === t.id" class="diagnostics-panel">
          <div v-if="diagnosticsLoading" style="color: #9fb0c7; font-size: 14px;">Loading…</div>
          <div v-else-if="!diagnosticsData[t.id]" style="color: #fca5a5; font-size: 14px;">Failed to load diagnostics</div>
          <template v-else>
            <div class="diag-row">
              <span>Engine State</span>
              <span>{{ diagnosticsData[t.id].connectionStatus ?? '—' }}</span>
            </div>
            <div class="diag-row">
              <span>Engine Hours</span>
              <span>{{ diagnosticsData[t.id].engineHours ?? '—' }}</span>
            </div>
            <div class="diag-row">
              <span>Odometer</span>
              <span>{{ diagnosticsData[t.id].odometer != null ? diagnosticsData[t.id].odometer + ' mi' : '—' }}</span>
            </div>
            <div class="diag-row">
              <span>Speed</span>
              <span>{{ diagnosticsData[t.id].speedMph != null ? diagnosticsData[t.id].speedMph + ' mi/h' : '—' }}</span>
            </div>
            <div class="diag-row">
              <span>Engine Speed</span>
              <span>{{ diagnosticsData[t.id].rpm != null ? diagnosticsData[t.id].rpm + ' RPM' : '—' }}</span>
            </div>
            <div class="diag-row">
              <span>Fuel Level</span>
              <div class="diag-bar-group">
                <div class="diag-bar"><div class="diag-bar-fill" :style="{ width: (diagnosticsData[t.id].fuelLevel ?? 0) + '%' }"></div></div>
                <span>{{ diagnosticsData[t.id].fuelLevel != null ? diagnosticsData[t.id].fuelLevel + '%' : '—' }}</span>
              </div>
            </div>
            <div class="diag-row">
              <span>Def Level</span>
              <div class="diag-bar-group">
                <div class="diag-bar"><div class="diag-bar-fill" :style="{ width: (diagnosticsData[t.id].defLevel ?? 0) + '%' }"></div></div>
                <span>{{ diagnosticsData[t.id].defLevel != null ? diagnosticsData[t.id].defLevel + '%' : '—' }}</span>
              </div>
            </div>
            <div class="diag-row">
              <span>DRIVE</span>
              <span class="hos-badge hos-drive">{{ driverDetail[t.driverId]?.driveTime?.remainingFormatted ?? '—' }}</span>
            </div>
            <div class="diag-row">
              <span>SHIFT</span>
              <span class="hos-badge hos-shift">{{ driverDetail[t.driverId]?.shiftTime?.remainingFormatted ?? '—' }}</span>
            </div>
            <div class="diag-row">
              <span>BREAK</span>
              <span class="hos-badge hos-break">{{ driverDetail[t.driverId]?.breakTime?.remainingFormatted ?? '—'  }}</span>
            </div>
            <div class="diag-row">
              <span>CYCLE</span>
              <span class="hos-badge hos-cycle">{{ driverDetail[t.driverId]?.cycleTime?.remainingFormatted ?? '—' }}</span>
            </div>
          </template>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>

import {driverViewById, fetchTrucks, fetchVehicleState} from '@/services/ApiService'

export default {
  name: "TruckList",
  data() {
    return {
      filters: {status: "all", sort: "asc"},
      items: [],
      error: null,
      refreshTimer: null,
      isFirstLoad: true ,  // ✅ track first load only
      expandedTruck: null,
      diagnosticsData: {},
      driverDetail: {},
      diagnosticsLoading: false,
      driveTime: 0,
      breakTime: 0,
      shiftTime: 0,
      cycleTime: 0
    };
  },
  props: {
    companyId: { type: String, required: true },
  },
  computed: {
    sortedAndFiltered() {
      let arr =
          this.filters.status === "all"
              ? this.items
              : this.items.filter((t) => t.state === this.filters.status);
      arr = [...arr].sort((a, b) => {
        const an = a.number.toString().padStart(6, "0");
        const bn = b.number.toString().padStart(6, "0");
        return this.filters.sort === "asc"
            ? an.localeCompare(bn)
            : bn.localeCompare(an);
      });
      return arr;
    },
  },
  methods: {
    async loadTrucks() {
      try {
        const newItems = await fetchTrucks(this.companyId);
        this.items = newItems;         // ✅ swap data only, no global loader
        this.error = null;
      } catch (e) {
        this.error = e?.message || 'Failed to load trucks';
      } finally {
        this.isFirstLoad = false;      // first load is done
      }
    },
    async toggleDiagnostics(truckId, driverId) {
      // collapse if already open
      if (this.expandedTruck === truckId) {
        this.expandedTruck = null;
        return;
      }

      this.expandedTruck = truckId;
      this.diagnosticsLoading = true;
      await this.loadDriverDetail(driverId);

      try {
        const state = await fetchVehicleState(truckId);
        this.diagnosticsData = { ...this.diagnosticsData, [truckId]: state };

        console.log(this.diagnosticsData);

      } catch (e) {
        this.diagnosticsData = { ...this.diagnosticsData, [truckId]: null };
      } finally {
        this.diagnosticsLoading = false;
      }
    },
    async loadDriverDetail(driverId) {
      try {
        const state = await driverViewById(driverId);
        this.driverDetail = { ...this.driverDetail, [driverId]: state };
      } catch (e) {
        this.driverDetail = { ...this.driverDetail, [driverId]: null };
      }
    },
    speedLabel(t) {
      if (t.state === "offline") return "offline";
      if (t.speed === 0 && (t.state === "idle" || t.state === "online"))
        return t.state;
      return `${t.speed}mph`;
    },
    speedClass(t) {
      if (t.state === "offline") return "muted";
      if (t.state === "idle") return "idle";
      if (t.state === "driving") return "driving";
      return "ok";
    },
    pillClass(t) {
      if (t.statusShort === "D") return "pill-driving";
      if (t.statusShort === "ON") return "pill-on";
      if (t.statusShort === "OFF") return "pill-off";
      if (t.statusShort === "SB") return "pill-sleep";
      return "";
    },
  },
  mounted() {
    this.loadTrucks();
    this.refreshTimer = setInterval(() => {
      this.loadTrucks();
    }, 30000);
  },
  beforeUnmount() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  },
  watch: {
    companyId() {
      this.items = [];
      this.isFirstLoad = true;
      this.expandedTruck = null;
      this.loadTrucks();
    },
  },
};
</script>


<style scoped>
@import './TruckList.css';
</style>
